import { useTypedDispatch, useTypedSelector } from "App/Store";
import AddQualifier from "components/AddQualifier";
import  Brackets  from "components/Brackets/Bracket";
import RoundRobinBracket from "components/RoundRobinFixture";
import { DEFAULT_API_ERROR } from "core/constants/Defaults";
import { Seed } from "core/models/Bracket";
import {
  addRoundRobinResult,
  bracketUserCheckIn,
  updateResult,
  updateRoundRobinResult,
  updateSchedule,
} from "core/services/Bracket";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { bracketDetails } from "redux/action/bracketDetailsAction";
import { getRoundRobinResult } from "redux/action/bracketRoundRobinResultAction";
import { getBracketTeamsDetails } from "redux/action/bracketTeamActionAction";
import { hideLoader, showLoader } from "redux/action/loaderAction";
import { useNotification } from "shared/hooks/useNotification";

interface UrlParams {
  playoffId: string;
}

export default function BracketFullScreen() {
  const { playoffId } = useParams<UrlParams>();
  const dispatch = useTypedDispatch();

  const [playoffOpen, setPlayoffOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState("schedule-bracket");
  const [selectedSeed, setSelectedSeed] = useState<Seed>();
  const showNotification = useNotification();

  const {
    brackets,
    tournament,
    isLoading: isBracketsLoading,
  } = useTypedSelector((state) => state.brackets);

  const { bracketTeams, isLoading: isTeamsLoading } = useTypedSelector(
    (state) => state.bracketTeams
  );

  const { bracketResult, isLoading: isBracketResultLoading } = useTypedSelector(
    (state) => state.bracketResult
  );

  useEffect(() => {
    if (!playoffId) return;

    const fetchBracketData = async () => {
      dispatch(showLoader());
      try {
        await dispatch(bracketDetails(playoffId));
      } finally {
        dispatch(hideLoader());
      }
    };

    fetchBracketData();
  }, [dispatch, playoffId]);

  useEffect(() => {}, [brackets]);

  const handleMatchClick = (seed: Seed) => {
    setSelectedTab("schedule-bracket");
    setSelectedSeed(seed);
    setPlayoffOpen(true);
  };

  useEffect(() => {
    if (selectedSeed) {
      dispatch(getBracketTeamsDetails(selectedSeed.bracketId));

      if (tournament?.bracketType === "Round Robin") {
        dispatch(getRoundRobinResult(selectedSeed.bracketId));
      }
    }
  }, [dispatch, selectedSeed]);

  useEffect(() => {
    if (selectedSeed && bracketTeams?.length) {
      setPlayoffOpen(true);
    }
  }, [selectedSeed, bracketTeams, bracketResult]);

  const handleClose = () => setPlayoffOpen(false);

  const handleSubmit = (values: any) => {
    if (!selectedSeed) return;

    const { bracketId } = selectedSeed;

    const showSuccess = (msg: string) =>
      showNotification({ title: "Success", message: msg, type: "success" });
    const showError = (msg?: string) =>
      showNotification({
        title: "Error",
        message: msg || DEFAULT_API_ERROR,
        type: "error",
      });

    const refreshBracket = () =>
      playoffId && dispatch(bracketDetails(playoffId));

    const processResponse = (res: any, successMsg: string) => {
      if (res.status) {
        showSuccess(successMsg);
        setPlayoffOpen(false);
        refreshBracket();
      } else {
        showError(res.message);
      }
      dispatch(hideLoader());
    };

    dispatch(showLoader());

    if (selectedTab === "schedule-bracket") {
      const schedulePayload = {
        bracketId,
        matchStart: values.startTime,
        checkInMinutes: values.checkInMinutes,
        gressTime: values.gressTime || 10,
      };
      updateSchedule(schedulePayload).then((res) =>
        processResponse(res, "Schedule Updated Successfully")
      );
      return;
    }

    if (selectedTab === "add-result") {
      const isRoundRobin = tournament?.bracketType === "Round Robin";

      if (isRoundRobin && bracketResult?.length && selectedSeed?.isCompleted) {
        const updatedResults = Object.entries(values.matchFormat || {}).map(
          ([matchKey, matchData]: [string, any]) => ({
            match: Number(matchKey),
            results: matchData.results.map((r: any) => ({
              teamId: Number(r.teamId),
              score: Number(r.score),
            })),
            playerResults: matchData.playerResults.map((p: any) => ({
              userId: Number(p.userId),
              stats: Object.fromEntries(
                Object.entries(p.stats || {}).map(([k, v]) => [k, Number(v)])
              ),
            })),
          })
        );

        const payload = { bracketId, updatedResults };
        updateRoundRobinResult(payload).then((res) =>
          processResponse(res, "Result Updated Successfully")
        );
      } else if (isRoundRobin) {
        // add new round robin result
        const payload = {
          id: bracketId,
          results: values.results?.map((r: any) => ({
            score: r.score,
            teamId: r.teamId,
          })),
          playerResults: values.playerResults?.map((p: any) => ({
            userId: p.userId,
            stats: p.stats,
          })),
        };
        addRoundRobinResult(payload).then((res) =>
          processResponse(res, "Round Robin Result Updated Successfully")
        );
      } else {
        const payload = {
          id: bracketId,
          results: values.results?.map((r: any) => ({
            score: r.score,
            teamId: r.teamId,
          })),
          playerResults: values.playerResults?.map((p: any) => ({
            userId: p.userId,
            stats: p.stats,
          })),
        };
        updateResult(payload).then((res) =>
          processResponse(res, "Result Updated Successfully")
        );
      }
    }
  };

  const processResponse = (res: any, successMsg: string) => {
    if (res.status) {
      showNotification({
        title: "Success",
        message: successMsg,
        type: "success",
      });
      setPlayoffOpen(false);
      dispatch(bracketDetails(playoffId));
    } else {
      showNotification({
        title: "Error",
        message: res.message || DEFAULT_API_ERROR,
        type: "error",
      });
    }
    dispatch(hideLoader());
  };
  function handleSubmitCheckIn(brackId: string, teamId: number) {
    let payload = {
      bracketId: brackId,
      teamId: teamId,
    };

    bracketUserCheckIn(payload).then((res) =>
      processResponse(res, "Checked In Successfully")
    );
  }

  const isLoading =
    isBracketsLoading || isTeamsLoading || isBracketResultLoading;

  useEffect(() => {
    if (isLoading) {
      dispatch(showLoader());
    } else {
      dispatch(hideLoader());
    }
  }, [isLoading]);
  
  return (
    <>
      <div className="bracket-screen-wrapper">
        <div className="bracket-grp-title">
          <Link to="/playoff/tournaments" className="nav-back">
            Playoff
          </Link>

          <Link
            to={`/playoff/bracket/details/${tournament?.id}`}
            className="nav-back"
          >
            <span className="nav-back"> / </span>
            Detail
          </Link>
          <span> / Bracket</span>
        </div>
      </div>

      <div style={{ position: "relative", width: "100%", height: "100vh" }}>
        {!isLoading && brackets ? (
          tournament?.bracketType !== "Round Robin" ? (
            <Brackets brackets={brackets} onMatchClick={handleMatchClick} />
          ) : (
            <RoundRobinBracket
              brackets={brackets}
              onMatchClick={handleMatchClick}
            />
          )
        ) : (
          <div style={{ textAlign: "center", paddingTop: "2rem" }}>
            Loading brackets...
          </div>
        )}
      </div>

      {!isLoading &&
        bracketTeams &&
        selectedSeed &&
        tournament &&
        playoffOpen && (
          <AddQualifier
            show={playoffOpen}
            onClose={handleClose}
            onSubmit={handleSubmit}
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
            bracketTeams={bracketTeams}
            seed={selectedSeed}
            tournament={tournament}
            bracketResult={bracketResult || []}
            handleSubmitCheckIn={handleSubmitCheckIn}
          />
        )}
    </>
  );
}
